import type { SeriesRef } from './dicomweb';
import type { LoaderContext } from './loader';
import { fetchRendered } from './net';

/** Edge of the square thumbnail tile, in CSS pixels. */
export const THUMBNAIL_SIZE = 96;

/**
 * Thumbnail source for a series: always its middle instance (middle frame for multi-frame),
 * like the IMEXHS series tiles.
 * parity: full-size WADO-URI JPEG (contentType=image/jpeg), which is what IMEXHS requests.
 * fast: WADO-RS rendered at tile size (?viewport=), a few KB instead of a full-resolution JPEG,
 * and cacheable because pixels never change for a UID.
 */
export function thumbnailUrl(ctx: LoaderContext, series: SeriesRef): string {
  const instance = series.instances[Math.floor(series.instances.length / 2)];
  const frame = Math.max(Math.floor(instance.numberOfFrames / 2), 1);
  if (ctx.mode === 'parity') {
    const params = new URLSearchParams({
      requestType: 'WADO',
      studyUID: series.studyInstanceUID,
      seriesUID: series.seriesInstanceUID,
      objectUID: instance.sopInstanceUID,
      contentType: 'image/jpeg',
    });
    if (instance.numberOfFrames > 1) params.set('frameNumber', String(frame));
    return `${ctx.wadoUriRoot}?${params.toString()}`;
  }
  const size = THUMBNAIL_SIZE * Math.ceil(window.devicePixelRatio || 1);
  const framePath = instance.numberOfFrames > 1 ? `/frames/${frame}` : '';
  return `${ctx.client.instanceUrl(series, instance)}${framePath}/rendered?viewport=${size},${size}`;
}

/** Fetches the thumbnail with the viewer's headers (ticket) and puts it in `img`. */
export async function loadThumbnail(ctx: LoaderContext, series: SeriesRef, img: HTMLImageElement): Promise<void> {
  const response = await fetchRendered(thumbnailUrl(ctx, series), ctx.client.requestHeaders());
  if (!response.ok) throw new Error(`thumbnail ${response.status}`);
  const url = URL.createObjectURL(await response.blob());
  img.addEventListener('load', () => URL.revokeObjectURL(url), { once: true });
  img.src = url;
}
